import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeft } from "lucide-react";
import type { FormProduct, ProductConfig, LocalisedProductConfig } from "@shared/types";
import type { ProductFormStatus } from "@bcc/components/shopify-import/product-form";
import type { IntensityComparisonCoffee } from "@bcc/components/ui/intensity-scale";
import { getFormProductKey } from "@bcc/components/shopify-import/get-form-product-key";
import { Accordion } from "@bcc/components/ui/accordion";
import { Button } from "@bcc/components/ui/button";
import { ProductFormCollapsible } from "./product-form-collapsible";
import { setConfigureStepTestId } from "./configure-step.utils";

export interface ConfigureStepProps {
  products: FormProduct[];
  productConfig: ProductConfig;
  localisedProductConfig: LocalisedProductConfig;
  isSingleBagSizeEnabled?: boolean;
  shortTermMaxWeeklyInventory?: number;
  shortTermMinWeeklyInventory?: number;
  longTermMinWeeklyInventory?: number;
  longTermMaxWeeklyInventory?: number;
  roasterNoteMaxLength?: number;
  productDescriptionMaxLength?: number;
  titleMaxLength?: number;
  regionLargeBagSize?: string[];
  marginFloorPercent?: number;
  /** The roaster's other coffees and their intensity scores, shown as
   *  comparison markers on the intensity scale. Optional — the scale renders
   *  without markers when the list is empty. */
  comparisonCoffees?: readonly IntensityComparisonCoffee[];
  /** Duplicate-name errors keyed by product uuid (computed per product by the modal). */
  titleErrors?: Record<string, string | undefined>;
  /** Returns to the Select step (rendered as a back button at the top). */
  onBack: () => void;
  onProductChange: (uuid: string, product: FormProduct) => void;
  onProductStatusChange: (uuid: string, status: ProductFormStatus) => void;
  /** Removes a single product from the batch without leaving Configure (§13.2). */
  onProductRemove: (uuid: string) => void;
  testId?: string;
}

/**
 * ConfigureStep — modal step 2. Renders one standalone collapsible ProductForm
 * per selected product (batch import). Each form owns its own RHF
 * instance; the modal coordinates validity and collected values via the
 * onProductChange / onProductStatusChange callbacks.
 */
export function ConfigureStep({
  products,
  titleErrors,
  onBack,
  onProductChange,
  onProductStatusChange,
  onProductRemove,
  testId,
  ...formProps
}: ConfigureStepProps) {
  const { t } = useTranslation();
  const allKeys = useMemo(() => products.map(getFormProductKey), [products]);
  // Control the open items so we can hide each form's body via CSS (keeping the
  // form mounted) instead of letting Radix unmount it. All start expanded.
  const [openKeys, setOpenKeys] = useState<string[]>(allKeys);

  return (
    <div data-testid={testId ?? setConfigureStepTestId("default")} className="space-y-4">
      {/* Back to the Select step lives at the top-right so the roaster can change
          the selection without scrolling past every form. */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground" data-testid={setConfigureStepTestId("count")}>
          {t("views.shopifyImportModal.configure.productCount", { count: products.length })}
        </p>
        <Button variant="ghost" size="sm" onClick={onBack} data-testid={setConfigureStepTestId("back")}>
          <ChevronLeft className="mr-1 h-4 w-4" />
          {t("buttons.back")}
        </Button>
      </div>
      <Accordion type="multiple" value={openKeys} onValueChange={setOpenKeys} className="space-y-4">
        {products.map((product, index) => {
          const key = getFormProductKey(product);
          return (
            <ProductFormCollapsible
              key={key}
              {...formProps}
              product={product}
              index={index}
              titleError={titleErrors?.[key]}
              isOpen={openKeys.includes(key)}
              onChange={onProductChange}
              onStatusChange={onProductStatusChange}
              onRemove={onProductRemove}
            />
          );
        })}
      </Accordion>
    </div>
  );
}
